import type { BufferGeometry } from 'three';
import { ImportedModelOverlayGeometrySnapshot } from '../highlight/geometry-internal';

/**
 * 选择显示的几何模板：整 Mesh 借用正式几何，只有精确 group 子集才创建独占缓存。
 * 子集不借用单独的 BufferAttribute，避免释放派生 geometry 时误删原模型的 GPU 属性。
 */
export class ImportedModelSelectionGeometryTemplate {
    /** 当前独占的 primitive 子集，完整选择时为空。 */
    private subset: BufferGeometry | null = null;
    /** 子集创建时的源布局版本。 */
    private snapshot: ImportedModelOverlayGeometrySnapshot | null = null;
    /** 已排序、去重的子集身份。 */
    private primitiveKey = '';

    /**
     * 获取可绘制的选择几何，不修改源 groups、drawRange 或顶点。
     * @param source 正式渲染几何。
     * @param indices 局部 group 子集，undefined 表示完整选择。
     * @returns 外部借用或本模板拥有的几何。
     */
    public resolve(source: BufferGeometry, indices?: readonly number[]): BufferGeometry {
        const groups = source.groups;
        if (!indices || groups.length === 0 || indices.length === groups.length) {
            this.dispose();
            return source;
        }
        const key = indices.join(',');
        if (this.subset && this.primitiveKey === key && this.snapshot?.matches(source)) return this.subset;
        // 子集缓存只在选择范围或正式几何版本变化时生成，不在渲染循环中切割数组。
        const next = source.clone();
        next.clearGroups();
        for (const index of indices) {
            const group = groups[index];
            if (group) next.addGroup(group.start, group.count, 0);
        }
        const snapshot = new ImportedModelOverlayGeometrySnapshot(source);
        this.dispose();
        this.subset = next;
        this.snapshot = snapshot;
        this.primitiveKey = key;
        return next;
    }

    /** 释放独占子集，不释放源 geometry 或其属性。 */
    public dispose(): void {
        this.subset?.dispose();
        this.subset = null;
        this.snapshot = null;
        this.primitiveKey = '';
    }
}
